import { languages, LanguageCode } from '@app/types';
import axios from 'axios';
import Cookies from 'js-cookie';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Define the structure of your context
interface LanguageContextType {
    loading: boolean;
    language: LanguageCode;
    setLanguage: (language: LanguageCode) => void;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | null>(null);

export const getLanguageFromI18n = (i18nLanguage: string): LanguageCode => {
    if (i18nLanguage === 'en') return 'default';
    const language = languages.find((lang) => lang.code === i18nLanguage);
    if (language) return language.code;

    if (i18nLanguage.includes('-')) {
        const [lang] = i18nLanguage.split('-');
        const language = languages.find((l) => l.code === lang);
        if (language) return language.code;
    }
    return 'default';
};

// Provider component
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState<LanguageCode>(getLanguageFromI18n(i18n.language) as LanguageCode);
    const [loading, setLoading] = useState(false);

    const changeLanguage = (lang: LanguageCode) => {
        setLoading(true);
        i18n.changeLanguage(lang === 'default' ? 'en' : lang).then(() => {
            setLanguage(lang);
            setLoading(false);
        });
    };

    /*useEffect(() => {
        if (i18n.language !== language) {
            i18n.changeLanguage(language === 'default' ? 'en' : language).then();
        }
    }, []);*/

    // Update lang attribute on the HTML element when language changes
    useEffect(() => {
        document.documentElement.lang = language === 'default' ? 'en' : language;

        axios.defaults.headers.common['X-Locale'] = language;
        Cookies.set('locale', language, { expires: 365, path: '/' });
    }, [language]);

    return <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, loading }}>{children}</LanguageContext.Provider>;
};

// Custom hook for easy access
export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
